import { useMemo, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { get } from 'lodash-es'

import { MapShape } from '@/feature/map'
import {
  GEO_FEATURE_MARKER_COLOR,
  GEOFENCE_FIELDS,
  HIGH_RISK_MARKER_COLOR,
  useObservatoryPageStore,
} from '@/feature/observatory'
import { hexToRgba } from '@/feature/observatory/utils/hexToRgba'
import { resolveLinestringColor, useRgbCycleShapes } from '@/feature/observatory/utils/useRgbCycleColor'
import { GeoFence } from '@/feature/observatory/types/GeoFence.types'
import { useDeferredLineStringBuffer } from '@/feature/observatory/utils/lineStringBuffer'

interface LineStringGeoFenceShapeProps {
  geoFence?: GeoFence
}

export const LineStringGeoFenceShape = ({ geoFence }: LineStringGeoFenceShapeProps) => {
  const { watch } = useFormContext()
  const map = useObservatoryPageStore((state) => state.map?.map)

  const highRisk = watch(GEOFENCE_FIELDS.highRisk)
  const radius = watch(GEOFENCE_FIELDS.radius)
  const rawColor = watch(GEOFENCE_FIELDS.color) as string | undefined
  const customColor = resolveLinestringColor(rawColor, GEO_FEATURE_MARKER_COLOR)

  const polylineRef = useRef<google.maps.Polyline>(null)
  const bufferRef = useRef<google.maps.Polygon>(null)

  // Drive RGB cycling directly on Google Maps shapes — no React re-renders
  useRgbCycleShapes(rawColor, polylineRef, bufferRef)

  const lineCoords = useMemo(
    () =>
      (get(geoFence, 'geoJson.geometry.coordinates', []) as number[][])
        .filter((coord) => Array.isArray(coord) && coord.length >= 2)
        .map((coord: number[]) => ({
          lat: Number(coord[1]),
          lng: Number(coord[0]),
        }))
        .filter((coord) => isFinite(coord.lat) && isFinite(coord.lng)),
    [geoFence],
  )

  // Build a simple buffered polygon (corridor) around the line using Google geometry
  // Deferred: polyline renders immediately, buffer computes async & hides below zoom 12.5
  const bufferMeters = radius ?? get(geoFence, 'geoJson.properties.radius', 50)
  const bufferPolygon = useDeferredLineStringBuffer(lineCoords, bufferMeters, map)

  const hasCustomColor = !!rawColor && rawColor.toLowerCase() !== 'rgb'
  const strokeColor = highRisk
    ? HIGH_RISK_MARKER_COLOR
    : customColor
  const fillColor = highRisk
    ? '#FF000040'
    : hasCustomColor
      ? hexToRgba(customColor, 0.3)
      : '#1945B64D'

  if (!geoFence || !map || lineCoords.length === 0) return null

  return (
    <>
      {/* Buffer area */}
      {bufferPolygon.length ? (
        <MapShape
          ref={bufferRef}
          shape={window.google.maps.Polygon}
          map={map}
          parameters={{
            map,
            paths: [bufferPolygon],
            strokeWeight: 0,
            fillColor,
            fillOpacity: 1,
            zIndex: 9999,
            editable: false,
            draggable: false,
            clickable: false,
          }}
        />
      ) : null}

      {/* LineString (rendered as polyline) */}
      <MapShape
        ref={polylineRef}
        shape={window.google.maps.Polyline}
        map={map}
        parameters={{
          map,
          path: lineCoords,
          strokeColor,
          strokeWeight: 3,
          geodesic: true,
          editable: false, // Read-only
          draggable: false, // Read-only
          clickable: false,
          zIndex: 10000,
        }}
      />
    </>
  )
}
